import {
  getCookieValue,
  setCookie,
  deleteCookie,
  clearCart as clearCookieCart,
  clearWishlist as clearCookieWishlist,
} from "./cookieUtils";
import type { UserInfo } from "../store/authSlice";
import type { AppDispatch } from "../store/store";

// Minimal JWT decode (no verification) to read exp claim
export const decodeJwt = (token: string | null) => {
  if (!token) return null;
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(decodeURIComponent(escape(decoded)));
  } catch (e) {
    return null;
  }
};

export const isJwtValid = (token: string | null) => {
  const payload = decodeJwt(token);
  if (!payload || !payload.exp) return false;
  const now = Math.floor(Date.now() / 1000);
  return payload.exp > now;
};

export const saveAuthCookies = (jwt: string, pksoftToken: string, username: string, userId: string) => {
  setCookie("JWT", jwt, 7);
  setCookie("x-auth", pksoftToken, 7);
  setCookie("username", username, 7);
  setCookie("userid", userId, 7);
};

export const clearAuthCookies = () => {
  deleteCookie("JWT");
  deleteCookie("x-auth");
  deleteCookie("username");
  deleteCookie("userid");
};

export const readAuthFromCookies = () => {
  return {
    jwt: getCookieValue("JWT"),
    pksoft: getCookieValue("x-auth"),
    username: getCookieValue("username"),
    userid: getCookieValue("userid"),
  };
};

/**
 * Save guest ID to storage layers: cookie, localStorage, sessionStorage
 * Called after successful guest login
 */
export const saveGuestId = (guestId: string) => {
  setCookie("guest_id", guestId, 30); // 30 days for guest session
  localStorage.setItem("backup_guest_id", guestId); // Secondary backup
  sessionStorage.setItem("session_guest_id", guestId); // Tertiary backup (session-only)
  // (IndexedDB removed)
  //console.log("[AUTH] Guest ID saved to all storage layers");
};

/**
 * Get guest ID with 4-layer fallback:
 * 1. Cookie (fast, 30 days)
 * 2. localStorage (survives session, even if cookies cleared)
 * 3. sessionStorage (session-only, survives clearing both above)
 * (IndexedDB removed)
 */
export const getGuestId = async (): Promise<string | null> => {
  // Try cookie first
  const cookieValue = getCookieValue("guest_id");
  if (cookieValue) return cookieValue;

  // Try localStorage backup
  const localValue = localStorage.getItem("backup_guest_id");
  if (localValue) {
    //console.log("[AUTH] Guest ID recovered from localStorage");
    return localValue;
  }

  // Try sessionStorage backup
  const sessionValue = sessionStorage.getItem("session_guest_id");
  if (sessionValue) {
    //console.log("[AUTH] Guest ID recovered from sessionStorage");
    return sessionValue;
  }

  // IndexedDB fallback removed — rely on cookie/localStorage/sessionStorage only

  return null;
};

/**
 * Delete guest ID from all 4 locations
 * Called on logout or when converting guest to user
 */
export const deleteGuestId = () => {
  deleteCookie("guest_id");
  localStorage.removeItem("backup_guest_id");
  sessionStorage.removeItem("session_guest_id");
  //console.log("[AUTH] Guest ID removed from all storage layers");
};

/**
 * Save user ID to storage layers: cookie, localStorage, sessionStorage
 * Called after successful user registration/login
 */
export const saveUserId = (userId: string) => {
  setCookie("user_id", userId, 30); // 30 days for user session
  localStorage.setItem("backup_user_id", userId); // Secondary backup
  sessionStorage.setItem("session_user_id", userId); // Tertiary backup (session-only)
  // (IndexedDB removed)
  //console.log("[AUTH] User ID saved to all storage layers");
};

/**
 * Get user ID with 4-layer fallback:
 * 1. Cookie (fast, 30 days)
 * 2. localStorage (survives session, even if cookies cleared)
 * 3. sessionStorage (session-only, survives clearing both above)
 * (IndexedDB removed)
 */
export const getUserId = async (): Promise<string | null> => {
  // Try cookie first
  const cookieValue = getCookieValue("user_id");
  if (cookieValue) return cookieValue;

  // Try localStorage backup
  const localValue = localStorage.getItem("backup_user_id");
  if (localValue) {
    //console.log("[AUTH] User ID recovered from localStorage");
    return localValue;
  }

  // Try sessionStorage backup
  const sessionValue = sessionStorage.getItem("session_user_id");
  if (sessionValue) {
    //console.log("[AUTH] User ID recovered from sessionStorage");
    return sessionValue;
  }

  // IndexedDB fallback removed — rely on cookie/localStorage/sessionStorage only

  return null;
};

/**
 * Delete user ID from all 4 locations
 * Called on logout
 */
export const deleteUserId = () => {
  deleteCookie("user_id");
  localStorage.removeItem("backup_user_id");
  sessionStorage.removeItem("session_user_id");
  //console.log("[AUTH] User ID removed from all storage layers");
};

/**
 * Handle after guest login - save guest_id and update Redux
 * @param guestId - The guest ID returned from backend
 * @param dispatch - Redux dispatch function
 */
export const handleGuestLoginSuccess = (guestId: string, dispatch: AppDispatch): void => {
  // Save to cookie
  saveGuestId(guestId);

  // Update Redux auth.userInfo
  dispatch({
    type: "auth/setUserInfo",
    payload: { guest_id: guestId },
  });

  //console.log("[AUTH] Guest login successful. Guest ID:", guestId);
};

/**
 * Handle after user registration/login - save user_id and update Redux
 * @param userId - The user ID returned from backend
 * @param userInfo - Full user info object from backend
 * @param dispatch - Redux dispatch function
 */
export const handleUserLoginSuccess = (userId: string, userInfo: UserInfo, dispatch: AppDispatch): void => {
  // Save to cookie
  saveUserId(userId);

  // Delete guest_id if exists (converting guest to user)
  deleteGuestId();

  // Update Redux auth.userInfo with full user data
  dispatch({
    type: "auth/setUserInfo",
    payload: { ...userInfo, user_id: userId },
  });

  //console.log("[AUTH] User login successful. User ID:", userId);
};

/**
 * Clear all auth data when user logs out
 */
export const handleLogout = (dispatch: AppDispatch): void => {
  // Clear all auth cookies
  clearAuthCookies();
  deleteGuestId();
  deleteUserId();

  // Clear Cart and Wishlist cookies
  clearCookieCart();
  clearCookieWishlist();

  // Clear Redux auth
  dispatch({
    type: "auth/clearUserInfo",
    payload: null,
  });

  // Clear Redux Cart
  dispatch({ type: "cart/clearCart", payload: null });

  // Clear Redux Wishlist
  dispatch({ type: "wishlist/clearWishlist", payload: null });

  // Clear Redux API Counts
  dispatch({ type: "apiCounts/clearCounts", payload: null });

  // Remove only owned auth keys — never call localStorage.clear()
  // which would nuke third-party lib state and redux-persist encrypted data
  const AUTH_LS_KEYS = ["backup_user_id", "backup_guest_id"];
  AUTH_LS_KEYS.forEach((k) => localStorage.removeItem(k));
  sessionStorage.removeItem("session_user_id");
  sessionStorage.removeItem("session_guest_id");

  //console.log("[AUTH] User logged out. All auth, cart, wishlist data cleared.");
};

export default {
  decodeJwt,
  isJwtValid,
  saveAuthCookies,
  clearAuthCookies,
  readAuthFromCookies,
  saveGuestId,
  getGuestId,
  deleteGuestId,
  saveUserId,
  getUserId,
  deleteUserId,
  handleGuestLoginSuccess,
  handleUserLoginSuccess,
  handleLogout,
};
